import aat from "@/data/aat";
import internal from "@/data/internal-ontologies";
import {
  findAndGetAATValue,
  extractImageUuid,
  getManifestUrls,
  getCreationDateTimespan,
  getImageUrls,
  getName,
  getRightsStatementId as getSharedRightsStatementId
} from "@thegetty-private/linked-data.js";

import * as wkt from "wkt";
import * as configHelper from "@/helpers/configHelper";
import {
  getClassifiedAs,
  getClassified,
  getValueByClassification,
  getValuesByClassification,
  normalizeFieldToArray,
  getFieldPartSubfield,
  getObjectParts,
  getValueOrContent
} from "@thegetty/linkedart.js";

const ASSIGNED = "assigned";
const ASSIGNED_BY = "assigned_by";
const DIMENSION = "dimension";
const IDENTIFIED_BY = "identified_by";
const REFERRED_TO_BY = "referred_to_by";
const REFERS_TO = "refers_to";
const PRODUCED_BY = "produced_by";
const TOOK_PLACE_AT = "took_place_at";
const PART = "part";
const INFLUENCED = "influenced";
const PARTICIPANT = "participant";
const IIIF_V2 =
  'application/ld+json;profile="http://iiif.io/api/presentation/2/context.json"';

// activity types that may have attributed roles
const ATTRIBUTED_ACTIVITY_TYPES = [
  "Activity",
  "Acquisition",
  "Production",
  "TransferOfCustody"
];

/**
 * Takes an elasticSearch object and transforms it into the cards
 *
 * @param {object} object - The elasticsearch object
 *
 * @return {object} The card fields
 */
export function getCardFields(object) {
  return {
    creation_date: object.date_created,
    parents: object.parentTree
  };
}

/**
 * Takes a ManMadeObject and gets the camera bearing angle of the view cone
 *
 * @param {object} object - The JSON-LD for ManMadeObject
 * @return {string} The camera bearing angle
 */
export function getCameraBearing(object) {
  let result = undefined;
  if (!object || !object.produced_by) {
    return result;
  }
  const producedBy = object.produced_by;
  const referred = normalizeFieldToArray(producedBy, REFERRED_TO_BY);
  const viewCone = getClassifiedAs(referred, internal.VIEW_CONE);
  const dimensions = normalizeFieldToArray(viewCone[0], DIMENSION);
  if (!dimensions.length) {
    return result;
  }
  const cameraBearing = getValueByClassification(
    dimensions,
    internal.CAMERA_BEARING
  );

  result = cameraBearing.toString();
  return result;
}

/**
 * Gets the order from a HMO
 *
 * @param {object} object - The JSON-LD for a HMO
 *
 * @return {number} the order number (the position within the object's direct parent)
 */
export function getObjectOrder(object) {
  let order = getValueByClassification(
    normalizeFieldToArray(object, DIMENSION),
    aat.SEQUENCE
  );
  if (order != undefined) {
    return order;
  }

  // JPC Model fallback
  return getAssignedObjectOrder(object);
}

/**
 * Gets the order from a HMO
 *
 * @param {object} object - The JSON-LD for a HMO
 *
 * @return {number} the order number (the position within the object's direct parent)
 */
export function getAssignedObjectOrder(object) {
  let order = undefined;
  normalizeFieldToArray(object, ASSIGNED_BY).forEach((assigned_by) => {
    normalizeFieldToArray(assigned_by, ASSIGNED).forEach((assigned) => {
      order = getValueByClassification(assigned, aat.SEQUENCE);
    });
  });
  return order;
}

/**
 * Gets the aat classifications of a HumanMadeObject
 *
 * @param {object} object - The JSON-LD for ManMadeObject
 * @param {array} aatQualifiers - an array of AAT JSON-LD Documents to reference
 * @return {array} An array of string AAT values for the object's classifications
 */
export function getClassifications(object, aatQualifiers, lang_) {
  let result = [];
  const classifiedAs = object["classified_as"];
  if (!classifiedAs) {
    return result;
  }
  classifiedAs.forEach((item) => {
    let aatValue = findAndGetAATValue({
      id: item.id,
      qualifiers: aatQualifiers,
      lang: lang_
    });
    if (aatValue) {
      result.push(aatValue);
    }
  });
  return result;
}

/**
 * Gets the Date Created from the HumanMadeObject's Production
 *
 * @param {Object} object  - HumanMadeObject
 *
 * @returns {Array} the date created
 */
export function getDateCreated(object) {
  const refers_to = [...normalizeFieldToArray(object, REFERS_TO), object];

  let dates = refers_to.reduce((result, hmo) => {
    return result.concat(getCreationDateTimespan(hmo));
  }, []);

  let results = [];
  dates.forEach((date) => {
    // filter out machine generated dates, but leave dates that may have been marked as "machine generated", but as of 2/10/25 are human generated, but have no programmatic begin/end
    if (getClassified(date, internal.INFERRED_DATE_RANGE).length > 0) {
      return;
    }

    for (let val of [date, date.part_of]) {
      if (val == undefined) {
        continue;
      }
      if (
        // also had a test for computed dates
        getClassified(val, internal.INFERRED_DATE_RANGE).length == 0 ||
        (val.begin_of_the_begin == undefined && val.end_of_the_end == undefined)
      ) {
        normalizeFieldToArray(val, IDENTIFIED_BY).forEach((name) => {
          results.push(getValueOrContent(name));
        });
      }
    }
  });
  return results;
}

/**
 *
 * Gets the Date Created from the HumanMadeObject's Production
 *
 * @param {Object} object - HumanMadeObject
 *
 * @returns {Array} - an array of the created dates as timespan ranges (lte|gte)
 */
export function getDateCreatedAsTimespan(object) {
  const refers_to = [...normalizeFieldToArray(object, REFERS_TO), object];

  let results = [];
  refers_to
    .reduce((result, hmo) => result.concat(getCreationDateTimespan(hmo)), [])
    .forEach((result) => {
      if (result.part_of) {
        results.push(result.part_of);
      } else {
        results.push(result);
      }
    });
  return results.filter(
    (result) => getClassified(result, aat.COMPUTER_GENERATED).length == 0
  );
}

/**
 * Takes a ManMadeObject and get the Google Streetview urls
 *
 * @param {object} object - The JSON-LD for ManMadeObject
 * @return {array} An array of GeoJSON coordinates [{lat, lon}]
 */
export function getPlaceCreatedCoordinates(object) {
  let coordinates = [];
  let production_obj = object[PRODUCED_BY];
  let took_place = normalizeFieldToArray(production_obj, TOOK_PLACE_AT);
  if (!took_place.length) {
    return coordinates;
  }
  took_place.forEach((place_obj) => {
    // replacement of comma was a hack to make the WKT valid
    if (place_obj && place_obj["defined_by"] != undefined) {
      const parsedPlace = wkt.parse(place_obj["defined_by"].replace(",", ""));

      if (parsedPlace && parsedPlace.type == "Point") {
        const coords = {
          lat: parsedPlace.coordinates[1],
          lon: parsedPlace.coordinates[0]
        };
        coordinates.push(coords);
      }
    }
  });
  return coordinates;
}

/**
 * Takes a ManMadeObject and get the Google Streetview urls
 *
 * @param {object} object - The JSON-LD for ManMadeObject
 * @return {array} An array of string urls
 */
export function getGoogleStreetviewLink(object) {
  let links = [];
  const referredTo = normalizeFieldToArray(object, REFERRED_TO_BY);
  if (!referredTo.length) {
    return links;
  }
  let link_objs = getClassifiedAs(referredTo, internal.GOOGLE_STREETVIEW_LINK);
  if (link_objs.length < 0) {
    return links;
  }
  link_objs.forEach((identifier_obj) => {
    links.push(identifier_obj.id);
  });
  return links;
}

/**
 * Takes a ManMadeObject and get the Google Vision tags with confidence levels
 *
 * @param {object} object - The JSON-LD for ManMadeObject
 * @return {array} An array of objects [{tag:(string),confidence:(string num)}]
 */
export function getGoogleVisionTags(object) {
  let result = [];
  const referredToBy = normalizeFieldToArray(object, REFERRED_TO_BY);
  if (!referredToBy.length) {
    return result;
  }
  const googleVisonTags = getClassifiedAs(
    referredToBy,
    internal.GOOGLE_VISION_TAG
  );
  if (!googleVisonTags) {
    return result;
  }
  // build tagData objs and push to results
  googleVisonTags.forEach((obj) => {
    let tagData = {
      tag: obj.content
    };
    // get confidence score and add to tagData if it exists
    const dimensions = normalizeFieldToArray(obj, DIMENSION);
    const confidenceScore = getValueByClassification(
      dimensions,
      internal.CONFIDENCE_SCORE
    );
    if (confidenceScore) {
      tagData.confidence = confidenceScore.toString();
    }
    result.push(tagData);
  });
  return result;
}

/**
 * Takes a HumanMadeObject InformationObject and gets the metadata fields
 *
 * @param {object} object - The JSON-LD for a HumanMadeObject
 * @param {array} creators - Array of creator stub objects with paths
 * @param {array} _classifications - Array of classification aat value strings
 * @param {string} thumbUrl  - the iiif Image Thumbnail url
 * @param {function} translate - VueI18n translate
 *
 * @return {object} the metadata fields
 */
export function getMetadataFields({
  object,
  creators,
  classifications,
  thumbUrl,
  translate
} = {}) {
  if (object == undefined) {
    return {};
  }
  const filename = getRosettaFileName(object);
  const dateCreated = getDateCreated(object);
  const imageUrl = getImageUrls(object);
  let obj = {
    title: getName(object),
    primary_creator: creators,
    date_created: dateCreated,
    classifications: classifications,
    genre: getGenre(object),
    image_url: imageUrl,
    filename: filename,
    twelveSunsetsLink: getTwelveSunsetsLink(
      thumbUrl,
      dateCreated,
      filename,
      translate
    )
  };

  return obj;
}

/**
 * Looks at the Object, it's date and URL to determine whether it's part of 12 sunsets, if it is, it returns a link to the App, if not, it returns nothing
 * @param {String} imageUrl  - the iiif Url
 * @param {String} date  - the date created
 * @param {String} filename  - the rosetta filename
 * @param {function} translate - the VueI18n translate function (wrapped)
 *
 * @returns {String} a link to the 12 sunsets app
 */
export function getTwelveSunsetsLink(imageUrl, date, filename, translate) {
  // if the inputs are undefined or it's not a getty IIIF URL, or 12 sunsets is disabled, skip

  if (
    [imageUrl, date, filename].includes(undefined) ||
    imageUrl.indexOf("https://media.getty.edu/iiif/image/") < 0 ||
    configHelper.is12SunsetsDisabled()
  ) {
    return;
  }

  if (Array.isArray(date)) {
    date = date[0];
  }

  let prefixes = [
    "gri_2012_m_1_b005_fn007",
    "gri_2012_m_1_b003_fn001",
    "gri_2012_m_1_b003_fn002",
    "gri_2012_m_1_b003_fn004",
    "gri_2012_m_1_b005_fn006",
    "gri_2012_m_1_b005_fn005",
    "gri_2012_m_2_b008_fn001",
    "gri_2012_m_1_b010",
    "gri_2012_m_1_b021",
    "gri_2012_m_1_b001_f002",
    "gri_2012_m_1_fn3"
  ];

  let valid = false;
  // check whether the accession # is in the set of prefixes
  for (let prefix of prefixes) {
    if (filename && filename.startsWith(prefix)) {
      valid = true;
      break;
    }
  }
  if (valid == false) {
    return;
  }

  // construct the actual route/URL
  let thumbId = extractImageUuid(imageUrl);
  return [
    {
      href: "https://12sunsets.getty.edu/map/" + date + "/image/" + thumbId,
      name: translate("humanMadeObject.view_12_sunsets")
    }
  ];
}

/**
 * Uses the LinkedArt IIIF Helpers to get the manifest url from the HumanMadeObject
 * @param {Object} object - the HumanMadeObject
 *
 * @returns {String} the IIIF manifest URl
 */
export function getManifestUrl(object) {
  return getManifestUrls(object, { format: IIIF_V2 })[0];
}

/**
 * Takes a ManMadeObject and gets OCR (optical character recognition) text
 *
 * @param {object} object - The JSON-LD for ManMadeObject
 * @return {array} An array of string OCR text
 */
export function getOCRText(object) {
  const carries = normalizeFieldToArray(object, "carries");
  if (!carries.length) {
    return [];
  }
  return getValuesByClassification(carries, aat.OCR_TEXT);
}

/**
 * Uses the LinkedArt Helpers to get the name of the HumanMadeObject (Which is the page title)
 * @param {Object} object - the HumanMadeObject
 *
 * @returns {String} - the object name
 */
export function getPageTitle(object) {
  if (object == undefined) {
    return "";
  }
  return getName(object);
}

/**
 * Finds all of the references to the {Place} records (depicted) in the HumanMadeObject
 * @param {Object} object - the HumanMadeObject
 *
 * @returns {Array} - returns an array of Place records
 */
export function getPlaceDepictedRefs(object) {
  let placeDepicted = [];
  const depicts = normalizeFieldToArray(object, "depicts");
  depicts.forEach((obj) => {
    if (obj.type === "Place") {
      placeDepicted.push(obj);
    }
  });
  return placeDepicted;
}

/**
 * Takes a ManMadeObject and get the rights statement id/url.
 *
 * @param {object} object - The JSON-LD for ManMadeObject
 * @return {string} The rights statement id/url
 *
 */
export function getRightsStatementId(object) {
  return getSharedRightsStatementId(object, aat.RIGHTS_STATEMENT);
}

/**
 * Takes a HumanMadeObject and get the Rosetta File Name
 *
 * @param {object} object - The JSON-LD for ManMadeObject
 * @return {string} The Rosetta File Name
 *
 */
export function getRosettaFileName(object) {
  const identifiedBy = normalizeFieldToArray(object, IDENTIFIED_BY);
  if (!identifiedBy.length) {
    return undefined;
  }

  return getValueByClassification(identifiedBy, internal.ROSETTA_FILE_NAME);
}

/**
 * Strips all trailing commas from the text and trims it
 * @param {String} text_
 *
 * @returns {String}
 */
export function trimAndStripCommas(text_) {
  if (text_ == undefined || typeof text_ !== "string") {
    return text_;
  }

  let text = text_.trim();
  let tryAgain = true;
  // handle cases with trailing commas that may or may not be in markdown
  while (tryAgain) {
    if (text.endsWith(",*")) {
      text = text.substring(0, text.length - 2).trim() + "*";
    } else if (text.endsWith(",_")) {
      text = text.substring(0, text.length - 2).trim() + "_";
    } else if (text.endsWith(", *")) {
      text = text.substring(0, text.length - 3).trim() + "*";
    } else if (text.endsWith(", _")) {
      text = text.substring(0, text.length - 3).trim() + "_";
    } else if (text.endsWith(",")) {
      text = text.substring(0, text.length - 1).trim();
    } else {
      tryAgain = false;
    }
  }
  return text;
}

/**
 * Gets the actors with LOC relators as attributed roles from activities
 * according to ASpace -> Linked Art modeling
 *
 * @param {object} - Human Made Object LOD
 * @param {string} property - property key for activity to check for assigned attributed roles
 * @param {string} activityProperty - key on activity to check for assigned attributed roles
 *
 * @returns {array} actors from requested activity with added 'assigned'
 * key with LOC relator role
 */
export function getActorsWithRoles(
  object,
  property = INFLUENCED,
  activityProperty = PARTICIPANT
) {
  let actors = [];
  const parts = getObjectParts(object, property).filter((e) => {
    // filter by activity types attributed roles are mapped to
    return ATTRIBUTED_ACTIVITY_TYPES.includes(e.type);
  });

  const activities = [...parts, ...normalizeFieldToArray(object, property)];

  activities.forEach((activity) => {
    if (activity == undefined) {
      return;
    }
    const actor = activity[activityProperty];

    if (actor) {
      actors.push({
        ...actor,
        assigned: getFieldPartSubfield(activity, PART, ASSIGNED)
      });
    }
  });
  return actors;
}

/**
 * Takes a HumanMadeObject and get the genre (multidisciplinary concent)
 *
 * @param {object} object - The JSON-LD for ManMadeObject
 * @return {string} the genre
 *
 */
export function getGenre(object) {
  const referredToBy = normalizeFieldToArray(object, REFERRED_TO_BY);
  if (!referredToBy.length) {
    return undefined;
  }

  return getValueByClassification(referredToBy, aat.GENRE);
}
